/**
 * AllCommunicationsFeed
 * Universal unified inbox — merges notifications, file transfers, and inbox
 * messages into one chronological feed for every role.
 *
 * Features:
 *  - File card: download button, file name/size
 *  - Notification card: inline reply panel with prior replies shown
 *  - Message card: opens thread reply panel
 *  - Unread dot + mark-as-read on click
 *  - Unread count badge passed via onUnreadCount callback
 */

import { useState, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Bell,
  FileText,
  MessageSquare,
  Download,
  Reply,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Inbox,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface FeedItem {
  id: string;
  kind: "notification" | "file" | "message";
  title: string;
  body: string;
  senderName: string;
  senderRole: string;
  isRead: boolean;
  createdAt: Date | string;
  // file
  fileUrl?: string | null;
  fileName?: string | null;
  fileMimeType?: string | null;
  fileSize?: number | null;
  // notification
  notificationId?: number;
  replyCount?: number;
  // message
  threadId?: number;
}

interface Reply {
  id: number;
  senderName: string;
  senderRole: string;
  body: string;
  createdAt: Date | string;
}

interface Props {
  email: string;
  role: string;
  userName: string;
  userRole: string;
  onUnreadCount?: (count: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(d: Date | string) {
  const date = typeof d === "string" ? new Date(d) : d;
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatBytes(bytes: number | null | undefined) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function kindIcon(kind: FeedItem["kind"]) {
  if (kind === "file") return <FileText className="w-4 h-4 text-[oklch(0.55_0.14_75)]" />;
  if (kind === "message") return <MessageSquare className="w-4 h-4 text-blue-400" />;
  return <Bell className="w-4 h-4 text-[oklch(0.65_0.14_75)]" />;
}

function kindLabel(kind: FeedItem["kind"]) {
  if (kind === "file") return "File";
  if (kind === "message") return "Message";
  return "Notification";
}

// ─── ReplyPanel ───────────────────────────────────────────────────────────────
function ReplyPanel({
  notificationId,
  senderName,
  senderRole,
  senderEmail,
  onClose,
}: {
  notificationId: number;
  senderName: string;
  senderRole: string;
  senderEmail: string;
  onClose: () => void;
}) {
  const [body, setBody] = useState("");
  const repliesQuery = trpc.notifications.getReplies.useQuery({ notificationId });
  const replyMutation = trpc.notifications.reply.useMutation({
    onSuccess: () => {
      setBody("");
      repliesQuery.refetch();
      toast.success("Reply sent");
    },
    onError: () => toast.error("Failed to send reply"),
  });

  return (
    <div className="mt-3 border-t border-[oklch(0.88_0.01_240)] pt-3 space-y-3">
      {/* Prior replies */}
      {repliesQuery.data && repliesQuery.data.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
          {repliesQuery.data.map((r: Reply) => (
            <div key={r.id} className="bg-[oklch(0.93_0.008_240)] rounded-lg px-3 py-2 border border-[oklch(0.88_0.01_240)]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-[oklch(0.45_0.12_75)]">{r.senderName}</span>
                <span className="text-sm text-[oklch(0.45_0.03_240)]">{formatTime(r.createdAt)}</span>
              </div>
              <p className="text-sm text-[oklch(0.18_0.04_240)] whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Compose */}
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a reply…"
        className="text-sm min-h-[72px] bg-[oklch(0.93_0.008_240)] border-[oklch(0.82_0.01_240)] text-[oklch(0.08_0.01_240)] placeholder:text-[oklch(0.40_0.03_240)] resize-none"
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-[oklch(0.45_0.03_240)] hover:text-[oklch(0.18_0.04_240)]">
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!body.trim() || replyMutation.isPending}
          onClick={() =>
            replyMutation.mutate({
              notificationId,
              senderName,
              senderEmail,
              senderRole,
              body: body.trim(),
            })
          }
          className="bg-[oklch(0.55_0.14_75)] hover:bg-[oklch(0.48_0.14_75)] text-white"
        >
          {replyMutation.isPending ? "Sending…" : "Send Reply"}
        </Button>
      </div>
    </div>
  );
}

// ─── ThreadReplyPanel ─────────────────────────────────────────────────────────
function ThreadReplyPanel({
  threadId,
  senderName,
  senderEmail,
  senderRole,
  onClose,
  onSent,
}: {
  threadId: number;
  senderName: string;
  senderEmail: string;
  senderRole: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const [body, setBody] = useState("");
  const messagesQuery = trpc.inbox.getMessages.useQuery({ threadId });
  const addMessage = trpc.inbox.reply.useMutation({
    onSuccess: () => {
      setBody("");
      messagesQuery.refetch();
      onSent();
      toast.success("Message sent");
    },
    onError: () => toast.error("Failed to send message"),
  });

  return (
    <div className="mt-3 border-t border-[oklch(0.88_0.01_240)] pt-3 space-y-3">
      {messagesQuery.data && messagesQuery.data.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
          {messagesQuery.data.map((m: { id: number; senderName: string; body: string; createdAt: Date | string }) => (
            <div key={m.id} className="bg-[oklch(0.93_0.008_240)] rounded-lg px-3 py-2 border border-[oklch(0.88_0.01_240)]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-[oklch(0.35_0.14_220)]">{m.senderName}</span>
                <span className="text-sm text-[oklch(0.45_0.03_240)]">{formatTime(m.createdAt)}</span>
              </div>
              <p className="text-sm text-[oklch(0.18_0.04_240)] whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write a message…"
        className="text-sm min-h-[72px] bg-[oklch(0.93_0.008_240)] border-[oklch(0.82_0.01_240)] text-[oklch(0.08_0.01_240)] placeholder:text-[oklch(0.40_0.03_240)] resize-none"
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-[oklch(0.45_0.03_240)] hover:text-[oklch(0.18_0.04_240)]">
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!body.trim() || addMessage.isPending}
          onClick={() =>
            addMessage.mutate({
              threadId,
              senderName,
              senderEmail,
              isOutgoing: true,
              body: body.trim(),
            })
          }
          className="bg-[oklch(0.55_0.14_75)] hover:bg-[oklch(0.48_0.14_75)] text-white"
        >
          {addMessage.isPending ? "Sending…" : "Send"}
        </Button>
      </div>
    </div>
  );
}

// ─── FeedCard ─────────────────────────────────────────────────────────────────
function FeedCard({
  item,
  userName,
  userEmail,
  userRole,
  onMarkRead,
}: {
  item: FeedItem;
  userName: string;
  userEmail: string;
  userRole: string;
  onMarkRead: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showReply, setShowReply] = useState(false);

  const handleExpand = () => {
    setExpanded((v) => !v);
    if (!item.isRead) onMarkRead(item.id);
  };

  return (
    <div
      className={`rounded-xl border transition-all ${
        item.isRead
          ? "border-[oklch(0.88_0.01_240)] bg-[oklch(0.93_0.008_240)]"
          : "border-[oklch(0.72_0.14_75)/50%] bg-[oklch(0.95_0.012_85)]"
      }`}
    >
      {/* Header row */}
      <div
        className="flex items-start gap-3 p-4 cursor-pointer select-none"
        onClick={handleExpand}
      >
        {/* Unread dot */}
        <div className="mt-1 flex-shrink-0">
          {!item.isRead ? (
            <span className="block w-2 h-2 rounded-full bg-[oklch(0.65_0.14_75)]" />
          ) : (
            <span className="block w-2 h-2 rounded-full bg-transparent" />
          )}
        </div>

        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">{kindIcon(item.kind)}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[oklch(0.45_0.03_240)] uppercase tracking-wide">
              {kindLabel(item.kind)}
            </span>
            {item.replyCount != null && item.replyCount > 0 && (
              <Badge variant="outline" className="text-xs border-[oklch(0.55_0.14_75)/40%] text-[oklch(0.45_0.12_75)] py-0 px-1.5">
                {item.replyCount} {item.replyCount === 1 ? "reply" : "replies"}
              </Badge>
            )}
            <span className="text-sm text-[oklch(0.45_0.03_240)] ml-auto">{formatTime(item.createdAt)}</span>
          </div>
          <p className="text-base font-semibold text-[oklch(0.12_0.04_240)] mt-0.5 truncate">{item.title}</p>
          <p className="text-base text-[oklch(0.35_0.03_240)] mt-0.5 truncate">{item.body}</p>
          {item.kind === "file" && item.fileName && (
            <p className="text-base text-[oklch(0.45_0.12_75)] mt-0.5 truncate">
              📎 {item.fileName} {item.fileSize ? `(${formatBytes(item.fileSize)})` : ""}
            </p>
          )}
        </div>

        {/* Expand chevron */}
        <div className="flex-shrink-0 text-[oklch(0.55_0.02_240)]">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-[oklch(0.88_0.01_240)]">
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 text-sm text-[oklch(0.45_0.03_240)]">
              <span>From: <span className="text-[oklch(0.25_0.04_240)] font-semibold">{item.senderName}</span></span>
              <span>·</span>
              <span>{new Date(item.createdAt).toLocaleString()}</span>
            </div>
            <p className="text-base text-[oklch(0.18_0.04_240)] whitespace-pre-wrap leading-relaxed">{item.body}</p>

            {/* File download */}
            {item.kind === "file" && item.fileUrl && (
              <a
                href={item.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg bg-[oklch(0.72_0.14_75)/10%] hover:bg-[oklch(0.72_0.14_75)/20%] text-[oklch(0.45_0.12_75)] text-sm font-medium transition-colors border border-[oklch(0.72_0.14_75)/30%]"
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-4 h-4" />
                Download {item.fileName ?? "File"}
              </a>
            )}

            {/* Reply actions */}
            {!showReply && (
              <div className="flex gap-2 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[oklch(0.45_0.03_240)] hover:text-[oklch(0.55_0.14_75)] gap-1.5"
                  onClick={() => setShowReply(true)}
                >
                  <Reply className="w-3.5 h-3.5" />
                  Reply
                </Button>
              </div>
            )}

            {/* Reply panel */}
            {showReply && item.kind !== "message" && item.notificationId != null && (
              <ReplyPanel
                notificationId={item.notificationId}
                senderName={userName}
                senderRole={userRole}
                senderEmail={userEmail}
                onClose={() => setShowReply(false)}
              />
            )}
            {showReply && item.kind === "message" && item.threadId != null && (
              <ThreadReplyPanel
                threadId={item.threadId}
                senderName={userName}
                senderEmail={userEmail}
                senderRole={userRole}
                onClose={() => setShowReply(false)}
                onSent={() => setShowReply(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AllCommunicationsFeed({
  email,
  role,
  userName,
  userRole,
  onUnreadCount,
}: Props) {
  const feedQuery = trpc.notifications.unifiedFeed.useQuery(
    { email, role },
    { refetchInterval: 30000 }
  );

  const markReadMutation = trpc.notifications.markUnifiedRead.useMutation({
    onSuccess: () => feedQuery.refetch(),
  });

  const handleMarkRead = useCallback(
    (itemId: string) => {
      markReadMutation.mutate({ email, itemId });
    },
    [email, markReadMutation]
  );

  // Report unread count to parent
  useEffect(() => {
    if (!feedQuery.data || !onUnreadCount) return;
    const unread = feedQuery.data.filter((i: FeedItem) => !i.isRead).length;
    onUnreadCount(unread);
  }, [feedQuery.data, onUnreadCount]);

  const items: FeedItem[] = feedQuery.data ?? [];
  const unreadCount = items.filter((i) => !i.isRead).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-[oklch(0.65_0.14_75)]" />
          <span className="text-base font-semibold text-[oklch(0.12_0.04_240)]">All Communications</span>
          {unreadCount > 0 && (
            <Badge className="bg-[oklch(0.55_0.14_75)] text-white text-xs px-1.5 py-0 h-5">
              {unreadCount}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-[oklch(0.45_0.03_240)] hover:text-[oklch(0.55_0.14_75)] p-1"
          onClick={() => feedQuery.refetch()}
          disabled={feedQuery.isFetching}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${feedQuery.isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {feedQuery.isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-[oklch(0.93_0.008_240)] animate-pulse border border-[oklch(0.88_0.01_240)]" />
            ))}
          </div>
        )}

        {!feedQuery.isLoading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-[oklch(0.55_0.02_240)]">
            <Inbox className="w-8 h-8 mb-2" />
            <p className="text-base">No communications yet</p>
          </div>
        )}

        {items.map((item) => (
          <FeedCard
            key={item.id}
            item={item}
            userName={userName}
            userEmail={email}
            userRole={userRole}
            onMarkRead={handleMarkRead}
          />
        ))}
      </div>
    </div>
  );
}
